import { useEffect, useState } from 'react'

import { PropertySchema } from '@/components/json-schema-generator/types.ts'

import { PropertyForm } from './property-form.tsx'
import {
  addProperty,
  createEmptySchema,
  removeProperty,
  updateProperty,
} from './schema-utils.tsx'

export type JsonSchemaBuilderProps = {
  schema?: PropertySchema
  onChange?: (schema: PropertySchema) => void
}

export function JSONSchemaBuilder({
  schema: initialSchema,
  onChange,
}: JsonSchemaBuilderProps) {
  const [schema, setSchema] = useState<PropertySchema>(
    initialSchema ?? createEmptySchema()
  )
  const [newPropertyName, setNewPropertyName] = useState('')

  useEffect(() => {
    if (initialSchema) {
      setSchema(initialSchema)
    } else {
      setSchema((prevSchema) =>
        addProperty(prevSchema, newPropertyName, 'string')
      )
      setNewPropertyName('')
    }
  }, [initialSchema, newPropertyName])

  useEffect(() => {
    if (schema) {
      const propName = Object.keys(schema.properties ?? {})[0]
      const data = {
        $id: propName,
        ...(schema.properties?.[propName] as PropertySchema),
        // Include the required information from the parent schema
        required: schema.required || [],
      }

      onChange?.(data)
    }
  }, [onChange, schema])

  const handleUpdateProperty = (
    path: string[],
    updatedProperty: PropertySchema
  ) => {
    setSchema((prevSchema) => updateProperty(prevSchema, path, updatedProperty))
  }

  const handleRemoveProperty = (path: string[]) => {
    setSchema((prevSchema) => removeProperty(prevSchema, path))
  }

  const handleRenameProperty = (oldPath: string[], newName: string) => {
    setSchema((prevSchema) => {
      const newSchema = structuredClone(prevSchema)
      let current = newSchema
      for (let i = 0; i < oldPath.length - 1; i++) {
        const segment = oldPath[i]
        // @ts-expect-error I know TODO
        current =
          segment === 'items' ? current[segment] : current.properties?.[segment]
      }
      const oldName = oldPath[oldPath.length - 1]
      current.properties = current.properties ?? {}
      current.properties[newName] = {
        // @ts-expect-error TODO
        ...current.properties[oldName],
        $id: newName,
      }
      delete current.properties?.[oldName]
      if (current.required) {
        current.required = current.required.map((name: string) =>
          name === oldName ? newName : name
        )
      }
      return newSchema
    })
  }

  return (
    <div>
      <div className="space-y-6">
        {Object.entries(schema.properties ?? {}).map(([name, property]) => (
          <PropertyForm
            key={name}
            name={name}
            property={property as PropertySchema}
            path={[name]}
            onUpdate={handleUpdateProperty}
            onRemove={handleRemoveProperty}
            onRename={handleRenameProperty}
            parent={schema}
          />
        ))}
      </div>
    </div>
  )
}
